import { createContext, useContext } from 'react';

/**
 * Every word the registration form shows, in English and Bengali.
 *
 * The form is often filled in with the participant sitting beside the Centre Manager, so the
 * page can switch wholesale into Bengali script with the toggle at the top. Only the
 * **interface** changes — what is typed into the fields is stored exactly as typed.
 *
 * ---------------------------------------------------------------------------------------
 * THE BENGALI NEEDS A NATIVE READ BEFORE IT IS RELIED ON
 * ---------------------------------------------------------------------------------------
 *
 * First drafted 2026-11-05 without a Bengali speaker in the loop. The consent wording in
 * particular mirrors the paper notice and consent form, which Katja and Ants settled against
 * the DPDPA — a participant agreeing to a mistranslated sentence has not agreed to the real
 * one. Have it checked, and correct it here: this is the only place it lives.
 *
 * Context rather than props, defaulting to English, because the profile editor renders the same
 * consent and guardian sections. It never provides a language, so it is untouched by any of this.
 */

export type RegistrationLang = 'en' | 'bn';

const en = {
    // --- page ---
    addPerson: 'Add person',
    editPerson: 'Edit person',
    newFormSwitch: 'New registration form',

    // --- already enrolled? ---
    enrolledQuestion: 'Is this person already enrolled?',
    enrolledNo: 'No — a new person',
    enrolledYes: 'Yes — already enrolled',
    idNumberLabel: 'Their ID number',
    idNumberPlaceholder: 'e.g. SG103',
    find: 'Find',
    notFound: (id: string) => `Nobody with ID number ${id} is enrolled in this organisation.`,
    cannotEdit: (name: string) => `Found ${name}, but you don't have permission to edit them.`,
    confirmTitle: 'Is this the right person?',
    loadDetails: 'Yes — load their details',
    notThem: 'No, search again',
    loadingPerson: 'Loading their details…',
    editingBanner: (name: string, id: string) =>
        `Editing ${name} (${id}). Saving updates their existing record — it does not create a new person.`,
    startOver: 'Start again with a new person',
    communitiesLocked: "Their ID number and communities come from their existing record and can't be changed here. Change communities on their profile.",
    possibleMatchTitle: 'This person may already be enrolled',
    possibleMatchBody: 'Someone with the same date of birth and a similar name is already in Joyya:',
    bornOn: (date: string) => `born ${date}`,
    editThemInstead: 'Edit them instead',
    noPermissionShort: "you can't edit them",

    // --- identity ---
    identity: 'Identity',
    name: 'Name',
    nameError: 'Please enter a name',
    preferredName: 'Preferred name',
    nameInScript: (scriptLabel: string) => `Name in ${scriptLabel}`,
    phone: 'Phone',
    phonePlaceholder: 'Phone number',
    noPhone: 'No phone',
    dob: 'Date of birth',
    dobError: 'Please enter a date of birth',
    gender: 'Gender',
    genders: {
        MALE: 'Male',
        FEMALE: 'Female',
        TRANSGENDER: 'Transgender',
        NOT_STATED: 'Prefer not to say',
    } as Record<string, string>,
    address: 'Address',
    photo: 'Photo',
    photoKeepHint: 'Leave empty to keep their current photo.',

    // --- communities ---
    loadingCommunities: 'Loading communities…',
    noFormula: "This organisation has no identifier formula this form can use, so people can't be added here. Use the classic form.",
    communities: 'Communities',
    communitiesPlaceholder: 'Every community they belong to',
    communitiesHint: 'Pick every community, in any order. Their identifier is coded from their location — Joyya Goods or another programme only codes it when they have no location.',
    homeLocation: 'Which is their home location?',
    identifier: 'Identifier',
    identifierPlaceholder: '(Allocated once a community is picked)',
    codedFrom: (community: string) => `Coded from ${community}.`,

    // --- consent ---
    consent: 'Consent',
    consentHint: 'The detail is on the participant\'s notice and consent form — these record what they answered. Leave anything not yet given unticked; people with gaps show up in the "Missing H&S / data consent" filter on the People page.',
    consentGroupPersonal: 'Personal Information',
    consentGroupHealth: 'Health and Safety',
    consentGroupId: 'ID',
    consentDataCollection: 'Consents to the collection and use of their information in reporting, social media, running programs, as set out in detail on the notice and consent form.',
    picturePermission: 'Picture permission',
    picturePermissionHint: 'This is yes if the above toggle is yes, unless the participant specifically indicated otherwise.',
    healthSafetyBriefed: 'Briefed on health and safety',
    consentHealthSafety: 'Consents to the health and safety risks of their program and agrees to comply with safety instructions.',
    idSightedByCM: 'Their ID has been sighted by the Centre Manager.',

    // --- guardian ---
    guardianTitle: 'Parent or guardian',
    guardianApplies: 'This person is under 18, so a parent or guardian gives consent on their behalf.',
    guardianNotApplies: 'Only needed for a person under 18. Enter a date of birth above to fill this in.',
    guardianName: 'Parent / guardian name',
    fullName: 'Full name',
    guardianDob: 'Parent / guardian date of birth',
    guardianRelationship: 'Relationship to the child',
    guardianRelationshipPlaceholder: 'Mother, father, grandmother, legal guardian…',
    guardianPhone: 'Parent / guardian phone',
    guardianMinorTitle: 'This parent is under 18',
    guardianMinorBody: 'Ask whether there is a parent or guardian over 18 who can give consent. If there is, record them here instead.',
    guardianIsMinorNoAlternative: 'Parent is a minor, and no alternative adult guardian is available to give consent',
    guardianConsentsToChildData: "Parent consents to the collection and use of the child's data",
    guardianId: "Parent's ID",
    guardianIdSighted: 'ID sighted by the Centre Manager, and it confirms the relationship to the child',
    guardianNoId: "No ID, but has confirmed to the Centre Manager's satisfaction that they are the named child's parent or guardian, and therefore have the right to give consent",
    guardianNoCopyHint: "We do not keep a copy of the parent's ID. If the parent is themselves a Joyya participant, their documents live on their own profile.",

    // --- emergency contact ---
    emergencyTitle: 'Emergency contact',
    emergencyHint: 'Who to call if something happens. Needed for everyone.',
    sameAsGuardian: 'Same as parent/guardian',
    emergencyName: 'Emergency contact name',
    emergencyPhone: 'Emergency contact number',
    emergencyPhoneError: 'Please enter a number',
    relationship: 'Relationship',
    emergencyRelationshipPlaceholder: 'Mother, husband, friend, neighbour…',

    // --- health ---
    healthTitle: 'Health information',
    healthHint: 'Anything about their health they have chosen to share. Optional. Only admins and social workers can see this on their profile afterwards.',
    healthLabel: 'Health information they have shared',

    // --- documents ---
    documentsTitle: 'Supporting documents',
    documentsHint: "For example a photo of their signature or thumbprint, the signed registration form, or a parental permission slip. Photographs or PDFs. Visible afterwards on this person's profile to admins, Centre Managers and social workers who work with them.",
    documentsOnFile: (n: number) => `${n} already on their profile.`,
    addDocument: 'Add a document',
    documentKinds: {
        THUMBPRINT_SIGNATURE: 'Thumbprint signature',
        PERMISSION_SLIP: 'Parental permission slip',
        REGISTRATION_FORM: 'Registration form',
        OTHER: 'Other',
    } as Record<string, string>,

    // --- saving ---
    stillNeeded: 'Still needed before saving',
    missing: {
        name: 'Name',
        dob: 'Date of birth',
        community: 'A community',
        chooseHome: 'Which location is their home',
        phone: 'Phone, or tick "No phone"',
        emergencyContactName: 'Emergency contact name',
        emergencyContactPhone: 'Emergency contact number',
        consentDataCollection: 'Consent to the collection and use of their information',
        healthSafetyBriefed: 'Briefed on health and safety',
        consentHealthSafety: 'Consent to the health and safety risks',
        guardianConsentsToChildData: "Parent or guardian's consent to the child's data",
    },
    save: 'Save',
    saveChanges: 'Save changes',
    cancel: 'Cancel',
    couldNotSave: 'Could not save',
    saveFailed: 'Something went wrong saving this person.',
    documentsFailed: (detail: string) => `Saved, but these documents did not upload — ${detail}`,
};

export type RegistrationText = typeof en;

const bn: RegistrationText = {
    // --- page ---
    addPerson: 'নতুন ব্যক্তি যোগ করুন',
    editPerson: 'ব্যক্তির তথ্য সম্পাদনা',
    newFormSwitch: 'নতুন নিবন্ধন ফর্ম',

    // --- already enrolled? ---
    enrolledQuestion: 'এই ব্যক্তি কি ইতিমধ্যে নথিভুক্ত?',
    enrolledNo: 'না — নতুন ব্যক্তি',
    enrolledYes: 'হ্যাঁ — ইতিমধ্যে নথিভুক্ত',
    idNumberLabel: 'তাঁর আইডি নম্বর',
    idNumberPlaceholder: 'যেমন SG103',
    find: 'খুঁজুন',
    notFound: (id) => `এই সংস্থায় ${id} আইডি নম্বরের কেউ নথিভুক্ত নেই।`,
    cannotEdit: (name) => `${name}-কে পাওয়া গেছে, কিন্তু তাঁর তথ্য সম্পাদনার অনুমতি আপনার নেই।`,
    confirmTitle: 'ইনিই কি সঠিক ব্যক্তি?',
    loadDetails: 'হ্যাঁ — তাঁর তথ্য খুলুন',
    notThem: 'না, আবার খুঁজুন',
    loadingPerson: 'তথ্য লোড হচ্ছে…',
    editingBanner: (name, id) =>
        `${name} (${id})-এর তথ্য সম্পাদনা করছেন। সংরক্ষণ করলে তাঁর বিদ্যমান রেকর্ড হালনাগাদ হবে — নতুন ব্যক্তি তৈরি হবে না।`,
    startOver: 'নতুন ব্যক্তি দিয়ে আবার শুরু করুন',
    communitiesLocked: 'আইডি নম্বর ও কমিউনিটি তাঁর বিদ্যমান রেকর্ড থেকে নেওয়া হয়েছে এবং এখানে বদলানো যায় না। কমিউনিটি বদলাতে তাঁর প্রোফাইলে যান।',
    possibleMatchTitle: 'এই ব্যক্তি সম্ভবত ইতিমধ্যে নথিভুক্ত',
    possibleMatchBody: 'একই জন্মতারিখ ও প্রায় একই নামের একজন ইতিমধ্যে Joyya-তে আছেন:',
    bornOn: (date) => `জন্ম ${date}`,
    editThemInstead: 'এর বদলে তাঁর তথ্য সম্পাদনা করুন',
    noPermissionShort: 'তাঁর তথ্য সম্পাদনার অনুমতি আপনার নেই',

    // --- identity ---
    identity: 'পরিচয়',
    name: 'নাম',
    nameError: 'অনুগ্রহ করে নাম লিখুন',
    preferredName: 'পছন্দের নাম',
    nameInScript: (scriptLabel) => (scriptLabel === 'Bengali' ? 'বাংলায় নাম' : `${scriptLabel}-এ নাম`),
    phone: 'ফোন',
    phonePlaceholder: 'ফোন নম্বর',
    noPhone: 'ফোন নেই',
    dob: 'জন্মতারিখ',
    dobError: 'অনুগ্রহ করে জন্মতারিখ লিখুন',
    gender: 'লিঙ্গ',
    genders: {
        MALE: 'পুরুষ',
        FEMALE: 'মহিলা',
        TRANSGENDER: 'ট্রান্সজেন্ডার',
        NOT_STATED: 'বলতে চান না',
    },
    address: 'ঠিকানা',
    photo: 'ছবি',
    photoKeepHint: 'বর্তমান ছবি রাখতে চাইলে ফাঁকা রাখুন।',

    // --- communities ---
    loadingCommunities: 'কমিউনিটি লোড হচ্ছে…',
    noFormula: 'এই সংস্থার এমন কোনো আইডি ফর্মুলা নেই যা এই ফর্ম ব্যবহার করতে পারে, তাই এখানে কাউকে যোগ করা যাবে না। পুরনো ফর্মটি ব্যবহার করুন।',
    communities: 'কমিউনিটি',
    communitiesPlaceholder: 'তিনি যেসব কমিউনিটির অন্তর্ভুক্ত, সবগুলো',
    communitiesHint: 'যেকোনো ক্রমে সব কমিউনিটি বেছে নিন। আইডি নম্বর তাঁর লোকেশন থেকে তৈরি হয় — লোকেশন না থাকলে তবেই Joyya Goods বা অন্য কোনো প্রোগ্রাম থেকে তৈরি হয়।',
    homeLocation: 'কোন লোকেশনটি তাঁর বাড়ি?',
    identifier: 'আইডি নম্বর',
    identifierPlaceholder: '(কমিউনিটি বেছে নিলে বরাদ্দ হবে)',
    codedFrom: (community) => `${community} থেকে তৈরি।`,

    // --- consent ---
    consent: 'সম্মতি',
    consentHint: 'বিস্তারিত অংশগ্রহণকারীর বিজ্ঞপ্তি ও সম্মতিপত্রে আছে — এখানে শুধু তাঁর উত্তরগুলো লেখা থাকে। যা এখনো দেওয়া হয়নি, তাতে টিক দেবেন না; যাঁদের তথ্যে ফাঁক আছে, তাঁরা People পেজের "Missing H&S / data consent" ফিল্টারে দেখা যাবেন।',
    consentGroupPersonal: 'ব্যক্তিগত তথ্য',
    consentGroupHealth: 'স্বাস্থ্য ও নিরাপত্তা',
    consentGroupId: 'পরিচয়পত্র',
    consentDataCollection: 'বিজ্ঞপ্তি ও সম্মতিপত্রে বিস্তারিত বলা অনুযায়ী, রিপোর্টিং, সামাজিক মাধ্যম ও প্রোগ্রাম পরিচালনায় তাঁর তথ্য সংগ্রহ ও ব্যবহারে সম্মতি দিয়েছেন।',
    picturePermission: 'ছবি তোলার অনুমতি',
    picturePermissionHint: 'উপরের টগলটি হ্যাঁ হলে এটিও হ্যাঁ, যদি না অংশগ্রহণকারী আলাদাভাবে অন্য কিছু জানিয়ে থাকেন।',
    healthSafetyBriefed: 'স্বাস্থ্য ও নিরাপত্তা বিষয়ে অবহিত করা হয়েছে',
    consentHealthSafety: 'তাঁর প্রোগ্রামের স্বাস্থ্য ও নিরাপত্তা ঝুঁকিতে সম্মতি দিয়েছেন এবং নিরাপত্তা নির্দেশনা মেনে চলতে রাজি আছেন।',
    idSightedByCM: 'সেন্টার ম্যানেজার তাঁর পরিচয়পত্র দেখেছেন।',

    // --- guardian ---
    guardianTitle: 'মা-বাবা বা অভিভাবক',
    guardianApplies: 'এই ব্যক্তির বয়স ১৮-র কম, তাই মা-বাবা বা অভিভাবক তাঁর পক্ষে সম্মতি দেবেন।',
    guardianNotApplies: 'শুধু ১৮ বছরের কম বয়সীদের জন্য প্রয়োজন। এটি পূরণ করতে উপরে জন্মতারিখ লিখুন।',
    guardianName: 'মা-বাবা / অভিভাবকের নাম',
    fullName: 'পুরো নাম',
    guardianDob: 'মা-বাবা / অভিভাবকের জন্মতারিখ',
    guardianRelationship: 'শিশুর সঙ্গে সম্পর্ক',
    guardianRelationshipPlaceholder: 'মা, বাবা, দাদি/নানি, আইনগত অভিভাবক…',
    guardianPhone: 'মা-বাবা / অভিভাবকের ফোন',
    guardianMinorTitle: 'এই অভিভাবকের বয়স ১৮-র কম',
    guardianMinorBody: 'জিজ্ঞেস করুন, ১৮ বছরের বেশি বয়সী এমন কোনো মা-বাবা বা অভিভাবক আছেন কি না যিনি সম্মতি দিতে পারেন। থাকলে তাঁর তথ্য এখানে লিখুন।',
    guardianIsMinorNoAlternative: 'অভিভাবক নিজে নাবালক, এবং সম্মতি দেওয়ার মতো অন্য কোনো প্রাপ্তবয়স্ক অভিভাবক নেই',
    guardianConsentsToChildData: 'অভিভাবক শিশুর তথ্য সংগ্রহ ও ব্যবহারে সম্মতি দিয়েছেন',
    guardianId: 'অভিভাবকের পরিচয়পত্র',
    guardianIdSighted: 'সেন্টার ম্যানেজার পরিচয়পত্র দেখেছেন, এবং তা শিশুর সঙ্গে সম্পর্ক নিশ্চিত করে',
    guardianNoId: 'পরিচয়পত্র নেই, তবে তিনিই যে উল্লিখিত শিশুর মা-বাবা বা অভিভাবক এবং তাই সম্মতি দেওয়ার অধিকার রাখেন, তা সেন্টার ম্যানেজারের কাছে সন্তোষজনকভাবে নিশ্চিত করেছেন',
    guardianNoCopyHint: 'আমরা অভিভাবকের পরিচয়পত্রের কোনো কপি রাখি না। অভিভাবক নিজেও Joyya-র অংশগ্রহণকারী হলে তাঁর নথিপত্র তাঁর নিজের প্রোফাইলে থাকে।',

    // --- emergency contact ---
    emergencyTitle: 'জরুরি যোগাযোগ',
    emergencyHint: 'কিছু ঘটলে কাকে ফোন করতে হবে। সবার জন্য প্রয়োজন।',
    sameAsGuardian: 'মা-বাবা/অভিভাবকের মতোই',
    emergencyName: 'জরুরি যোগাযোগের ব্যক্তির নাম',
    emergencyPhone: 'জরুরি যোগাযোগের নম্বর',
    emergencyPhoneError: 'অনুগ্রহ করে নম্বর লিখুন',
    relationship: 'সম্পর্ক',
    emergencyRelationshipPlaceholder: 'মা, স্বামী, বন্ধু, প্রতিবেশী…',

    // --- health ---
    healthTitle: 'স্বাস্থ্য সংক্রান্ত তথ্য',
    healthHint: 'স্বাস্থ্য নিয়ে তিনি নিজে যা জানাতে চেয়েছেন। ঐচ্ছিক। পরে শুধু অ্যাডমিন ও সমাজকর্মীরা তাঁর প্রোফাইলে এটি দেখতে পারবেন।',
    healthLabel: 'স্বাস্থ্য সম্পর্কে তিনি যা জানিয়েছেন',

    // --- documents ---
    documentsTitle: 'সহায়ক নথিপত্র',
    documentsHint: 'যেমন তাঁর স্বাক্ষর বা টিপসইয়ের ছবি, স্বাক্ষরিত নিবন্ধন ফর্ম, বা অভিভাবকের অনুমতিপত্র। ছবি বা PDF। পরে এই ব্যক্তির প্রোফাইলে অ্যাডমিন, সেন্টার ম্যানেজার এবং তাঁর সঙ্গে কাজ করা সমাজকর্মীরা দেখতে পারবেন।',
    documentsOnFile: (n) => `তাঁর প্রোফাইলে ইতিমধ্যে ${n}টি আছে।`,
    addDocument: 'নথি যোগ করুন',
    documentKinds: {
        THUMBPRINT_SIGNATURE: 'টিপসই',
        PERMISSION_SLIP: 'অভিভাবকের অনুমতিপত্র',
        REGISTRATION_FORM: 'নিবন্ধন ফর্ম',
        OTHER: 'অন্যান্য',
    },

    // --- saving ---
    stillNeeded: 'সংরক্ষণের আগে এগুলো প্রয়োজন',
    missing: {
        name: 'নাম',
        dob: 'জন্মতারিখ',
        community: 'একটি কমিউনিটি',
        chooseHome: 'কোন লোকেশনটি তাঁর বাড়ি',
        phone: 'ফোন, অথবা "ফোন নেই"-তে টিক দিন',
        emergencyContactName: 'জরুরি যোগাযোগের ব্যক্তির নাম',
        emergencyContactPhone: 'জরুরি যোগাযোগের নম্বর',
        consentDataCollection: 'তথ্য সংগ্রহ ও ব্যবহারে সম্মতি',
        healthSafetyBriefed: 'স্বাস্থ্য ও নিরাপত্তা বিষয়ে অবহিত করা',
        consentHealthSafety: 'স্বাস্থ্য ও নিরাপত্তা ঝুঁকিতে সম্মতি',
        guardianConsentsToChildData: 'শিশুর তথ্যে মা-বাবা বা অভিভাবকের সম্মতি',
    },
    save: 'সংরক্ষণ করুন',
    saveChanges: 'পরিবর্তন সংরক্ষণ করুন',
    cancel: 'বাতিল',
    couldNotSave: 'সংরক্ষণ করা যায়নি',
    saveFailed: 'এই ব্যক্তির তথ্য সংরক্ষণে সমস্যা হয়েছে।',
    documentsFailed: (detail) => `সংরক্ষণ হয়েছে, কিন্তু এই নথিগুলো আপলোড হয়নি — ${detail}`,
};

export const REGISTRATION_TEXT: Record<RegistrationLang, RegistrationText> = { en, bn };

const RegistrationLangContext = createContext<RegistrationLang>('en');

export const RegistrationLangProvider = RegistrationLangContext.Provider;

export const useRegistrationLang = (): RegistrationLang => useContext(RegistrationLangContext);

export const useRegistrationText = (): RegistrationText => REGISTRATION_TEXT[useRegistrationLang()];
